// IndexedDB-backed FIFO queue of FLAC clips waiting to upload to the server.
//
// Survives WebSocket drops, tab reloads, and tablet sleep. Drained by the
// uploader loop in Dashboard.tsx. Cap is enforced on enqueue: when full the
// oldest clip is evicted so the most recent recordings are always preserved.

const DB_NAME = 'engineAcoustics';
const DB_VERSION = 1;
const STORE = 'pending_uploads';

// 500 clips × ~250 KB = ~125 MB, well under any browser quota and ~2h of
// disconnect at the default 5s/15s cadence.
export const QUEUE_CAP = 500;

export type UnmixMethod = 'block' | 'ssa' | 'ica' | 'nmf';
export type MicLocation = 'phone_cab' | 'phone_hood' | 'external_hood';

export interface QueuedClip {
  /** IndexedDB autoincrement id, set on insert. */
  id?: number;
  /** Wall-clock ms when the clip was recorded — also used by the UI to
   *  correlate the queue entry to its in-memory ClipEntry. */
  clientId: number;
  createdAt: number;
  vehicleId: number;
  sampleRate: number;
  channels: number;
  durationSec: number;
  unmixMethod: UnmixMethod;
  flac: Uint8Array;
  /** Trip-scoped metadata captured at recording time. */
  tripStartKm: number;
  tripPurpose: string;
  micLocation: MicLocation;
  /** Engine description — needed by ICA, kept on every clip for record. */
  numCylinders: number;
  numStrokes: number;
  numValvesPerCylinder: number;
  /** OS-level label of the actually-attached audio track at record time. */
  micDeviceLabel?: string;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  return dbPromise;
}

function reqAsPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise<T>((res, rej) => {
    req.onsuccess = () => res(req.result);
    req.onerror = () => rej(req.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => Promise<T>,
): Promise<T> {
  const db = await openDb();
  const tx = db.transaction(STORE, mode);
  const result = await fn(tx.objectStore(STORE));
  return new Promise<T>((res, rej) => {
    tx.oncomplete = () => res(result);
    tx.onerror = () => rej(tx.error);
    tx.onabort = () => rej(tx.error);
  });
}

export interface EnqueueResult {
  id: number;
  evicted: number;
}

export async function enqueue(clip: Omit<QueuedClip, 'id'>): Promise<EnqueueResult> {
  return withStore('readwrite', async (store) => {
    const id = (await reqAsPromise(store.add(clip))) as number;
    let evicted = 0;
    let count = await reqAsPromise(store.count());
    while (count > QUEUE_CAP) {
      const cursor = await reqAsPromise(store.openCursor());
      if (!cursor) break;
      await reqAsPromise(cursor.delete());
      evicted++;
      count--;
    }
    return { id, evicted };
  });
}

export async function peekOldest(): Promise<QueuedClip | null> {
  return withStore('readonly', async (store) => {
    const cursor = await reqAsPromise(store.openCursor());
    return cursor ? (cursor.value as QueuedClip) : null;
  });
}

export async function deleteById(id: number): Promise<void> {
  await withStore('readwrite', async (store) => {
    await reqAsPromise(store.delete(id));
  });
}

export async function count(): Promise<number> {
  return withStore('readonly', (store) => reqAsPromise(store.count()));
}

export async function clear(): Promise<void> {
  await withStore('readwrite', async (store) => {
    await reqAsPromise(store.clear());
  });
}
